import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useEnvironment } from '../context/EnvironmentContext';
import styles from './EnvironmentToggle.module.css';

const SEGMENTS = Object.freeze([
  Object.freeze({ env: 'live', label: 'LIVE' }),
  Object.freeze({ env: 'sandbox', label: 'SANDBOX' })
]);

/**
 * Switching environment is a CROSS-ORIGIN navigation (a different origin serves a
 * different bundle against a different database), so the deep link has to be
 * rebuilt by hand — react-router cannot carry it.
 */
export const buildPeerHref = (peerUrl, { pathname, search }) =>
  `${peerUrl ?? ''}${pathname ?? ''}${search ?? ''}`;

/**
 * A page is dirty when something mounted says so. Pages opt in with
 * `data-dirty="true"`; nothing is inferred from arbitrary input values, because a
 * prefilled admin form would otherwise prompt on every single switch.
 */
const isPageDirty = () => document.querySelectorAll('[data-dirty="true"]').length > 0;

const formatBuiltAt = (builtAt) => {
  if (!builtAt || builtAt === 'unknown') return 'unknown';
  const parsed = new Date(builtAt);
  return Number.isNaN(parsed.getTime()) ? builtAt : parsed.toISOString().slice(0, 16).replace('T', ' ');
};

const EnvironmentToggle = () => {
  const { hasPermission } = useAuth();
  const { env, peerUrl, release, isLoading } = useEnvironment();
  const location = useLocation();

  // Render nothing until the origin has answered: a toggle that guesses LIVE and
  // then flips to SANDBOX is worse than one that appears a beat late.
  if (isLoading) return null;

  // Spec §LIVE/SANDBOX TOGGLE — the control exists only for holders of this key.
  if (!hasPermission('environment.switch')) return null;

  // No peer origin configured (PEER_ENV_URL unset): the inactive segment must emit
  // NO href at all. A relative `/admin` would be a same-origin link that a
  // ctrl/middle-click opens in a new tab, silently bypassing the onClick guard.
  const peerHref = buildPeerHref(peerUrl, location);

  const handleSwitch = (event, targetLabel) => {
    if (!peerUrl) {
      event.preventDefault();
      return;
    }
    if (isPageDirty()) {
      const shouldLeave = window.confirm(
        `You have unsaved changes on this page. Leave without saving and switch to ${targetLabel}?`
      );
      if (!shouldLeave) event.preventDefault();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toggle} role="group" aria-label="Environment">
        {SEGMENTS.map(({ env: segmentEnv, label }) => {
          const isActive = segmentEnv === env;
          const activeClass = segmentEnv === 'sandbox' ? styles.segmentSandboxActive : styles.segmentActive;

          return isActive ? (
            <span
              key={segmentEnv}
              className={`${styles.segment} ${activeClass}`}
              aria-current="true"
              data-environment-segment={segmentEnv}
              data-active="true"
            >
              {label}
            </span>
          ) : (
            <a
              key={segmentEnv}
              className={`${styles.segment} ${peerUrl ? '' : styles.segmentDisabled}`}
              href={peerUrl ? peerHref : undefined}
              onClick={(event) => handleSwitch(event, label)}
              aria-disabled={peerUrl ? undefined : 'true'}
              title={peerUrl ? `Switch to ${label} — ${peerHref}` : `${label} origin is not configured`}
              data-environment-segment={segmentEnv}
              data-active="false"
            >
              {label}
            </a>
          );
        })}
      </div>

      <span
        className={styles.release}
        title={`commit ${release?.commit ?? 'unknown'} · built ${release?.builtAt ?? 'unknown'}`}
        data-release-commit={release?.commit ?? 'unknown'}
        data-release-built-at={release?.builtAt ?? 'unknown'}
      >
        <span className={styles.releaseLabel}>BUILD</span>
        <span className={styles.releaseValue}>{(release?.commit ?? 'unknown').slice(0, 7)}</span>
        <span className={styles.releaseSep}>·</span>
        <span className={styles.releaseValue}>{formatBuiltAt(release?.builtAt)}</span>
      </span>
    </div>
  );
};

export default EnvironmentToggle;
